import { ChangeEventHandler, ReactNode, useMemo } from 'react';
import styles from './RadioToggleButton.module.css';
import EntryFilled from './svg/EntryFilled.svg';
import EntryUnfilled from './svg/EntryUnfilled.svg';

interface Props {
  children?: ReactNode;
  text?: string;
  subtext?: string;
  icon?: ReactNode;
  badgeText?: string;
  badgeStyle?: 'DEFAULT' | 'STYLE1';
  onChange?: ChangeEventHandler<HTMLInputElement>;
  selectedValue?: string;
  /** The group that this radio button belongs to. All components in the same
   * radioGroupName are part of the same input type=radio group. */
  radioGroupName: string;
  value: string;
  displayStyle?: 'TICKET';
  /** Renders as a plain button rather than a radio, for options that trigger an action instead of a selection. */
  onClick?: () => void;
}

const RadioToggleButton = (props: Props) => {
  // Ideally, this would use React's useId, however, it doesn't work in our
  // combination of NextJS and React versions
  const id = useMemo(() => {
    return 'RadioToggleButton_' + props.radioGroupName + '_' + props.value;
  }, [props.radioGroupName, props.value]);

  const isSelected = !props.onClick && props.selectedValue === props.value;

  const content = (
    <>
      {props.icon}
      {props.text} {props.children}{' '}
      {props.subtext && <span className={styles.subtext}>{props.subtext}</span>}
      {props.badgeText && (
        <div
          className={
            styles.badge +
            ' ' +
            styles['badgeStyle_' + (props.badgeStyle || 'DEFAULT')]
          }
        >
          {props.badgeText}
        </div>
      )}
    </>
  );

  return (
    <div
      className={`${styles.container} ${isSelected ? styles.checked : ''} ${props.displayStyle === 'TICKET' ? styles.ticketDisplay : ''}`}
    >
      {!props.onClick && (
        <input
          id={id}
          type="radio"
          name={props.radioGroupName}
          checked={isSelected}
          value={props.value}
          onChange={props.onChange}
        />
      )}
      {props.displayStyle === 'TICKET' ? (
        isSelected ? (
          <EntryFilled />
        ) : (
          <EntryUnfilled />
        )
      ) : null}
      {props.onClick ? (
        <button
          type="button"
          className={styles.actionLabel}
          onClick={props.onClick}
        >
          {content}
        </button>
      ) : (
        <label htmlFor={id}>{content}</label>
      )}
    </div>
  );
};

export default RadioToggleButton;
